import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Client from "../../utils/client";
import { api_url } from "../../constant";
import { useAuth } from "../../store/authProvider";
import PlaceOrderModal from "../../components/place_order_modal";
import { circle_add } from "../../icons/circle_add";
import { EmptyStarIcon, FullStarIcon } from "../../icons/star";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const InputDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [input, setInput] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { isAuth } = useAuth();
    const [showOrderModal, setShowOrderModal] = useState(false);
    const [favoriteOffers, setFavoriteOffers] = useState([]);
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: () => {
            return Client.post(`/favorites/${id}`, { favorite_type: "input" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries("favorites");
        },
    });

    useEffect(() => {
        const fetchInput = async () => {
            try {
                const response = await Client.get(`/inputs/${id}`).then((res) => res.data);
                setInput(response);
                
                // Fetch favorites if authenticated
                if (isAuth) {
                    const favorites = await Client.get("/favorites").then(res => res.data);
                    setFavoriteOffers(favorites);
                }

                await Client.post("/admin/logs", {
                    user_id: isAuth ? localStorage.getItem("user_id") : null,
                    action: "Product View",
                    product_type: "Inputs Product",
                    product_id: id.toString(),
                });
            } catch (error) {
                toast.error("فشل في تحميل بيانات المدخلات الزراعية");
            } finally {
                setLoading(false);
            }
        };
        fetchInput();
    }, [id, isAuth]);

    const addFavorites = async () => {
        if (!isAuth) {
            navigate("/signin");
            return;
        }
        mutation.mutate();
    };

    if (loading) {
        return <div className="flex justify-center items-center h-screen text-xl">جاري التحميل...</div>;
    }

    if (!input) {
        return <div className="flex justify-center items-center h-screen text-xl">المدخلات الزراعية غير موجودة</div>;
    }

    const images = input.images ? Object.values(input.images).filter(Boolean) : [];
    const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
    const prevImage = () => setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));

    const isFavorite = favoriteOffers.some((e) => e.id === parseInt(id));

    return (
        <div dir="rtl" className="mb-20 w-full py-10 px-4 lg:px-20">
            <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-lg p-8 relative">
                {/* Favorite Button */}
                <button 
                    onClick={addFavorites}
                    className="absolute left-6 top-6 z-10 p-2 bg-white rounded-full shadow-md"
                >
                    {isFavorite ? FullStarIcon : EmptyStarIcon}
                </button>

                <h1 className="text-4xl font-bold text-center mb-8 text-primary">{input.name}</h1>

                {/* Image Slider */}
                <div className="relative w-full h-[500px] overflow-hidden rounded-lg mb-8 border border-gray-200">
                    {images.length > 0 ? (
                        <>
                            <img
                                src={`${api_url}/${images[currentImageIndex]}`}
                                alt={`Input Image ${currentImageIndex + 1}`}
                                className="w-full h-full object-contain bg-gray-100"
                            />
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={nextImage}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                                    >
                                        &#10095;
                                    </button>
                                    <button
                                        onClick={prevImage}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 transition-all duration-200"
                                    >
                                        &#10094;
                                    </button>
                                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full text-sm">
                                        {currentImageIndex + 1} / {images.length}
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="w-full h-full flex justify-center items-center bg-gray-100 text-gray-500 text-xl">
                            لا توجد صور متاحة
                        </div>
                    )}
                </div>

                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <Detail label="النوع" value={input.input_category?.name} />
                    <Detail label="السعر" value={`${Number(input.price).toLocaleString()} جنيه سوداني`} />
                    <Detail label="الولاية" value={input._state?.name} />
                    {input._city && <Detail label="المدينة" value={input._city?.name} />}
                    {input._market && <Detail label="السوق" value={input._market?.name} />}
                    <Detail label="الوصف" value={input.description || "لا يوجد وصف"} />
                </div>

                {/* Technical Details */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-8">
                    <h2 className="text-2xl font-bold mb-6 text-primary border-b pb-2">المواصفات الفنية</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {input.ingredients && <Detail label="المكونات" value={input.ingredients} />}
                        {input.application && <Detail label="طريقة الاستخدام" value={input.application} />}
                        {input.dose && <Detail label="الجرعة" value={input.dose} />}
                        {input.cautions && <Detail label="التحذيرات" value={input.cautions} />}
                        {input.storing && <Detail label="صلاحية التخزين" value={input.storing} />}
                        {input.quality_certificate && <Detail label="شهادة الجودة" value={input.quality_certificate} />}
                    </div>
                </div>

                {/* Seller Information */}
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h2 className="text-2xl font-bold mb-6 text-primary border-b pb-2">معلومات البائع</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Detail label="الاسم" value={input.user?.name || "غير متوفر"} />
                        <Detail label="رقم الهاتف" value={input.user?.phonenumber || "غير متوفر"} />
                    </div>
                </div>

                {/* Order Button */}
                <div className="flex justify-center mt-8">
                    <button
                        className="px-6 flex justify-center gap-3 items-center py-3 text-lg font-bold text-white uppercase transition-all duration-150 ease-linear bg-primary rounded-lg shadow-md hover:bg-primary-600 hover:shadow-lg focus:outline-none"
                        type="button"
                        onClick={() => {
                            if (!isAuth) {
                                navigate("/signin");
                                return;
                            }
                            setShowOrderModal(true);
                            Client.post("/admin/logs", {
                                user_id: localStorage.getItem("user_id"),
                                action: "Product Order",
                                product_type: "Inputs Product",
                                product_id: input.id.toString(),
                            });
                        }}
                    >
                        <span>طلب المنتج</span>
                        {circle_add}
                    </button>
                </div>
            </div>

            {showOrderModal && (
                <PlaceOrderModal 
                    item={input} 
                    setShowOrderModal={setShowOrderModal} 
                    tradeType="Demand" 
                    negotiationType="Input" 
                />
            )}
        </div>
    );
};

const Detail = ({ label, value }) => (
    <div className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-sm transition-shadow duration-200">
        <p className="text-lg font-semibold text-gray-700 mb-1">{label}:</p>
        <p className="text-gray-600 text-justify">{value}</p>
    </div>
);

export default InputDetailsPage;